import * as Tone from 'tone';

export interface EffectParam {
  readonly key: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly default: number;
  /** True when the property is an AudioParam/Signal (set via .value). */
  readonly signal: boolean;
}

export interface EffectDef {
  readonly key: string;
  readonly label: string;
  readonly hasWet: boolean;
  readonly defaultWet?: number;
  /** Needs an AudioWorklet, which a file:// page cannot load. */
  readonly needsWorklet?: boolean;
  readonly params: readonly EffectParam[];
  readonly build: (state: EffectState) => Tone.ToneAudioNode;
}

export interface EffectState {
  enabled: boolean;
  wet?: number;
  [param: string]: number | boolean | undefined;
}

const n = (s: EffectState, k: string, fallback = 0): number =>
  typeof s[k] === 'number' ? (s[k] as number) : fallback;

export const EFFECTS: readonly EffectDef[] = [
  {
    key: 'chorus',
    label: 'Chorus',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'frequency', label: 'Rată (Hz)', min: 0.1, max: 10, step: 0.1, default: 1.5, signal: true }],
    build: (s) =>
      new Tone.Chorus({ frequency: n(s, 'frequency', 1.5), delayTime: 3.5, depth: 0.7, wet: n(s, 'wet', 0.4) }).start(),
  },
  {
    key: 'distortion',
    label: 'Distorsiune',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'distortion', label: 'Cantitate', min: 0, max: 1, step: 0.01, default: 0.4, signal: false }],
    build: (s) => new Tone.Distortion({ distortion: n(s, 'distortion', 0.4), wet: n(s, 'wet', 0.4) }),
  },
  {
    key: 'bitcrusher',
    label: 'BitCrusher',
    hasWet: true,
    defaultWet: 0.4,
    needsWorklet: true,
    params: [{ key: 'bits', label: 'Biți', min: 1, max: 8, step: 1, default: 4, signal: true }],
    // BitCrusher's constructor options do not accept `wet`; it is set after.
    build: (s) => {
      const node = new Tone.BitCrusher({ bits: n(s, 'bits', 4) });
      node.wet.value = n(s, 'wet', 0.4);
      return node;
    },
  },
  {
    key: 'vibrato',
    label: 'Vibrato',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'depth', label: 'Adâncime', min: 0, max: 1, step: 0.01, default: 0.3, signal: true }],
    build: (s) => new Tone.Vibrato({ frequency: 5, depth: n(s, 'depth', 0.3), wet: n(s, 'wet', 0.4) }),
  },
  {
    key: 'autowah',
    label: 'AutoWah',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'octaves', label: 'Octave', min: 1, max: 8, step: 0.5, default: 6, signal: false }],
    build: (s) =>
      new Tone.AutoWah({ baseFrequency: 100, octaves: n(s, 'octaves', 6), sensitivity: 0, wet: n(s, 'wet', 0.4) }),
  },
  {
    key: 'phaser',
    label: 'Phaser',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'frequency', label: 'Rată (Hz)', min: 0.1, max: 10, step: 0.1, default: 0.5, signal: true }],
    build: (s) =>
      new Tone.Phaser({ frequency: n(s, 'frequency', 0.5), octaves: 3, baseFrequency: 350, wet: n(s, 'wet', 0.4) }),
  },
  {
    key: 'pingpong',
    label: 'Ping-pong delay',
    hasWet: true,
    defaultWet: 0.35,
    params: [{ key: 'delayTime', label: 'Timp (s)', min: 0.05, max: 1, step: 0.01, default: 0.25, signal: true }],
    build: (s) =>
      new Tone.PingPongDelay({ delayTime: n(s, 'delayTime', 0.25), feedback: 0.2, wet: n(s, 'wet', 0.35) }),
  },
  {
    key: 'stereowidener',
    label: 'Stereo widener',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'width', label: 'Lățime', min: 0, max: 1, step: 0.01, default: 0.5, signal: true }],
    build: (s) => new Tone.StereoWidener({ width: n(s, 'width', 0.5), wet: n(s, 'wet', 0.4) }),
  },
  {
    key: 'eq3',
    label: 'EQ pe 3 benzi',
    hasWet: false,
    params: [
      { key: 'low', label: 'Grav (dB)', min: -20, max: 20, step: 1, default: 0, signal: true },
      { key: 'mid', label: 'Mediu (dB)', min: -20, max: 20, step: 1, default: 0, signal: true },
      { key: 'high', label: 'Acut (dB)', min: -20, max: 20, step: 1, default: 0, signal: true },
    ],
    build: (s) => new Tone.EQ3({ low: n(s, 'low'), mid: n(s, 'mid'), high: n(s, 'high') }),
  },
  {
    key: 'pitchshift',
    label: 'Pitch shift',
    hasWet: true,
    defaultWet: 0.4,
    params: [{ key: 'pitch', label: 'Semitonuri', min: -12, max: 12, step: 1, default: 0, signal: false }],
    build: (s) => new Tone.PitchShift({ pitch: n(s, 'pitch'), wet: n(s, 'wet', 0.4) }),
  },
];

export const EFFECTS_BY_KEY: Readonly<Record<string, EffectDef>> = Object.fromEntries(
  EFFECTS.map((d) => [d.key, d]),
);

export type EffectStates = Record<string, EffectState>;

export const defaultEffectStates = (): EffectStates => {
  const out: EffectStates = {};
  for (const def of EFFECTS) {
    const st: EffectState = { enabled: false };
    if (def.hasWet) st.wet = def.defaultWet ?? 0.4;
    for (const p of def.params) st[p.key] = p.default;
    out[def.key] = st;
  }
  return out;
};
